/**
 * @fileoverview
 * This file contains the logic for building the data context used in client portal logic evaluation.
 * It transforms data from the form datastore into a normalized structure that can be used
 * with JSON Logic rules.
 *
 * The main function `buildStepLogicDataContext` creates a context object with data from
 * different entities (case, company, individual) based on the client portal type.
 */
import { omit, snakeCase } from 'lodash';
import { ValueOf } from 'ts-essentials';
import { match } from 'ts-pattern';

import {
  type ClientPortalLogicContextType,
  ClientPortalTypeEnum,
  CompanyClassificationModel,
  companyDefaultPropertiesDefinition,
  CompanyTypeEnum,
  generateKeyFromMapping,
  matchFieldMapping,
  PropertyMapping,
  PropertyMappingEntityEnum,
  PropertyMappingTypeEnum,
} from '@dotfile/shared/domain';

import {
  FormDatastoreCase,
  FormDatastoreCompany,
  FormDatastoreIndividual,
  FormDatastoreState,
} from '../context/datastore/datastore';
import { FieldsValues } from '../utils';

/**
 * The data context used for evaluating client portal logic
 * This is a map of entity contexts (case, company, individual) to their property values
 */
export type LogicDataContext = Partial<
  Record<ClientPortalLogicContextType, FieldsValues | undefined>
>;

/**
 * Parameters for building the step logic data context
 */
export interface BuildStepLogicDataContextParams {
  /** Data from the form datastore */
  datastoreData: FormDatastoreState['data'];
  /** The type of client portal (KYB or KYC) */
  clientPortalType: ClientPortalTypeEnum;
}

/**
 * Builds a data context for evaluating step-level logic in the client portal
 *
 * This function takes data from the form datastore and transforms it into a normalized
 * structure that can be used with JSON Logic rules. The context includes data from
 * different entities (case, company, individual) based on the client portal type.
 *
 * @param params Configuration parameters including datastore data and client portal type
 * @returns A data context object with normalized entity data
 */
export function buildStepLogicDataContext({
  datastoreData,
  clientPortalType,
}: BuildStepLogicDataContextParams): LogicDataContext {
  const logicDataContext: LogicDataContext = {};

  // Add case data to the context if available
  if (datastoreData.case) {
    logicDataContext.case = normalizeDataToLogicContext(
      PropertyMappingEntityEnum.case,
      datastoreData.case,
    );
  }

  // Add entity-specific data based on client portal type
  match(clientPortalType)
    .with(ClientPortalTypeEnum.KYB, () => {
      // For KYB portals, add main company data
      const mainCompany = datastoreData.companies?.find(
        (c) => c.type === CompanyTypeEnum.main,
      );
      if (mainCompany) {
        logicDataContext.main_company = normalizeDataToLogicContext(
          PropertyMappingEntityEnum.company,
          mainCompany,
        );
      }
    })
    .with(ClientPortalTypeEnum.KYC, () => {
      // For KYC portals, add individual data
      const individual = datastoreData.individuals?.at(0);
      if (individual && individual.isBusinessContact) {
        logicDataContext.individual = normalizeDataToLogicContext(
          PropertyMappingEntityEnum.individual,
          individual,
        );
      }
    })
    .exhaustive();

  return logicDataContext;
}

/**
 * Normalizes entity data into a format suitable for logic context
 *
 * This function takes an entity object (case, company, individual) and transforms
 * its properties into a flat structure with standardized keys.
 *
 * @param entityType The type of entity being normalized
 * @param input The entity data to normalize
 * @returns A flat object with normalized property keys and values
 */
export function normalizeDataToLogicContext(
  entityType: PropertyMappingEntityEnum,
  input: FormDatastoreCase | FormDatastoreCompany | FormDatastoreIndividual,
): FieldsValues {
  if (!input) return {};

  // Process default properties (excluding custom properties)
  let resultRecord =
    Object.entries(omit(input, 'customProperties')).reduce(
      (acc, [propertyKey, propertyValue]) => {
        // Create a mapping of the property
        const mapping = {
          entity: entityType,
          type: PropertyMappingTypeEnum.default,
          key: snakeCase(propertyKey),
        };
        // Generate a standardized key for the property
        const key = generateKeyFromMapping(mapping);
        // Sanitize the property value
        const value = sanitizeValues(mapping, propertyValue);

        return { ...acc, [key]: value };
      },
      {},
    ) || {};

  // Process custom properties if present
  if (input.customProperties) {
    resultRecord = {
      ...resultRecord,
      ...Object.entries(input.customProperties).reduce(
        (acc, [propertyKey, value]) => {
          // Create a mapping of the custom property
          const mapping = {
            entity: entityType,
            type: PropertyMappingTypeEnum.custom,
            key: snakeCase(propertyKey),
          };
          // Generate a standardized key for the property
          const key = generateKeyFromMapping(mapping);
          return {
            ...acc,
            [key]: sanitizeValues(mapping, value as ValueOf<FieldsValues>),
          };
        },
        {},
      ),
    };
  }

  return resultRecord;
}

/**
 * Helper to match company classification properties
 */
const matchClassificationMapping = matchFieldMapping(
  companyDefaultPropertiesDefinition.classifications,
);

/**
 * Sanitizes property values for use in logic context
 *
 * This function performs several transformations on property values:
 * - Converts empty strings to null
 * - Recursively processes nested objects
 * - Handles special cases like company classifications to transform the object in a string
 *
 * @param mapping The property mapping information
 * @param value The property value to sanitize
 * @returns The sanitized value
 */
export function sanitizeValues(
  mapping: PropertyMapping,
  value: ValueOf<FieldsValues>,
): ValueOf<FieldsValues> {
  // Convert empty values to null
  if (
    typeof value !== 'boolean' && // except booleans which can be false
    (typeof value !== 'number' || isNaN(value)) && // except number that can be 0
    (!value || (typeof value === 'string' && value.trim() === ''))
  ) {
    return null;
  }

  // Recursively process nested objects
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.entries(value).reduce(
      (acc, [nestedKey, nestedValue]) => ({
        ...acc,
        [nestedKey]: sanitizeValues(
          { ...mapping, key: nestedKey, nested: true },
          nestedValue,
        ),
      }),
      {},
    );
  }

  // Handle special case for company classifications
  if (matchClassificationMapping({ mapping })) {
    value = normalizeCompanyDefaultClassificationsData(value);
  }

  return value;
}

/**
 * Normalizes company classification data for use in logic
 *
 * Transforms classification objects into string format: "type__code"
 *
 * @param value The classification data to normalize
 * @returns Normalized classification data
 */
function normalizeCompanyDefaultClassificationsData(
  value: ValueOf<FieldsValues>,
): ValueOf<FieldsValues> {
  if (!value || !Array.isArray(value) || !value.length) {
    return value;
  }

  if (!isCompanyClassificationModels(value)) {
    return value;
  }

  // Convert classification objects to strings in format "type__code"
  return value.map(({ type, code }) => `${type}__${code}`);
}

/**
 * Type guard to check if an array contains company classification models
 */
const isCompanyClassificationModels = (
  input: unknown,
): input is CompanyClassificationModel[] => {
  if (!Array.isArray(input) || !input.length) {
    return false;
  }

  return isCompanyClassificationModel(input[0]);
};

/**
 * Type guard to check if an object is a company classification model
 */
const isCompanyClassificationModel = (
  input: unknown,
): input is CompanyClassificationModel => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return false;
  }

  return 'type' in input && 'code' in input;
};
